import React, { useCallback, useContext, useMemo } from 'react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import CommonConstants from 'sg-common-constants';
import styles from 'saga-modules/sg-portals-wizard-react/Wizard/templates/SgWizardPageTemplate.module.scss';
import { Button } from '@jutro/components';
import messages from './SgIpidDetails.messages';

function SgIpidDetails(props) {
    const {
        data
    } = props;
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    const onClose = useCallback(() => {
        window.close();
    }, []);
    const renderListWithTitle = useCallback((list, id, cssClass) => {
        return (
            <div>
                {list.map((item) => {
                    const listTitle = (item.title && (item.keys.length > 0 || item.titleOnly)) && <h4>{item.title}</h4>;
                    return (
                        <div key={item.id ? `${item.id}Key` : `${item}Key`} id={item.id ? `${id}_${item.id}_Container` : `${id}_Container`}>
                            {listTitle}
                            <ol className={`${cssClass} ${data.Product}`}>
                                {item.keys.map((keys, index) => {
                                    return (
                                        <li key={keys} id={item.id ? `${id}_${item.id}_${index}` : `${id}_${index}`}>
                                            {keys}
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    );
                })}
            </div>
        );
    }, []);
    const renderList = useCallback((list, id, cssClass) => {
        return (
            <div>
                <ol className={cssClass}>
                    {list.map((keys, index) => {
                        return (
                            <li key={keys} id={`${id}_${index}`}>
                                {keys}
                            </li>
                        );
                    })}
                </ol>
            </div>
        );
    }, []);

    const renderInsuredList = useMemo(() => {
        return renderListWithTitle(data.insuredList, CommonConstants.IPID_PAGE.Insured, 'sgListWhatIsInsured');
    }, [data.insuredList, renderListWithTitle]);

    const renderNotInsuredList = useMemo(() => {
        return renderListWithTitle(data.notInsuredList, CommonConstants.IPID_PAGE.NotInsured, 'sgListWhatIsNotInsured');
    }, [data.notInsuredList, renderListWithTitle]);

    const renderRestrictionsList = useMemo(() => {
        return renderListWithTitle(data.restrictionsList, CommonConstants.IPID_PAGE.Restrictions, 'sgListRestrictionsOnCover');
    }, [data.restrictionsList, renderListWithTitle]);

    const renderCoveredList = useMemo(() => {
        return renderList(data.coveredList, CommonConstants.IPID_PAGE.Covered, 'sgListWhereCovered');
    }, [data.coveredList, renderList]);

    const renderObligationsList = useMemo(() => {
        return renderList(data.obligationsList, CommonConstants.IPID_PAGE.Obligations, 'sgListObligations');
    }, [data.obligationsList, renderList]);

    return (
        <div className={styles.digitalWizard}>
            <div className={`${styles.wizardMain} sgWizardMainLayout`}>
                <div className="sgIpidContainer">
                    <div className="sgProductInformationDocument" id="sgIpidSummaryBox">
                        <>
                            {!data.hideCloseButton && (
                                <div className="sgTopButtonContainer">
                                    <Button
                                        className="sgButton sgTransactionButton sgIpidTopButton"
                                        id="IpidCloseButtonTop"
                                        onClick={onClose}
                                    >
                                        {translator(messages.CloseButton)}
                                    </Button>
                                </div>
                            ) }
                        </>
                        <div className="sgPageHeading">
                            <h1
                                id={`ipidTitle_${data.header.suffix}`}
                            >
                                {data.header.title}
                            </h1>
                            <h2
                                id={`ipidTitle_${data.header.suffix}`}
                            >
                                {translator(messages.IpidInformation)}
                            </h2>
                            <div className="sgIpidCompanyProductContainer">
                                <h3 id={`ipidCompany_${data.header.suffix}`}>
                                    {translator(messages.IpidCompany)}
                                </h3>
                                <h3 id="ipidProduct_{{idSuffix}}">
                                    {translator(messages.Product)}
                                    { data.header.product }
                                </h3>
                            </div>
                            <p id={`ipidCompanyDetails_${data.header.suffix}`}>
                                {
                                    data.CompanyDetails ? data.CompanyDetails : translator(messages.IpidCompanyDetails)
                                }
                            </p>
                            {data.header.additionalParagraph
                                && (
                                    <p id={`ipidHeaderAdditionalParagraph_${data.header.suffix}`}>
                                        {data.header.additionalParagraph}
                                    </p>
                                )
                            }
                            {data.isSHECover && (
                                <p id={`ipidUnderwriterDetails_${data.header.suffix}`}>
                                    {translator(messages.IpidUnderwriterDetails)}
                                </p>
                            )}
                        </div>
                        <div className="sgInsuranceDetails">
                            <strong>{translator(messages.TermsAndConditionsText)}</strong>
                            <h3 id={`ipidTypeofInsuranceHeader_${data.header.suffix}`}>
                                {translator(messages.WhatTypeOfInsuranceHeading)}
                            </h3>
                            <p>{data.header.insuranceTypeDesc}</p>
                        </div>
                        <div className="sgFakeColumns">
                            <div className="sgDocumentSection sgHalfWidthLeft">
                                <div className="sgSectionContainer">
                                    <div className="sgSectionIcon sgIconWhatIsInsured">
                                        <h3 id={`ipidWhatIsInsurer_${data.header.suffix}`}>{translator(messages.WhatIsInsuredHeading)}</h3>
                                    </div>
                                    <div className="sgSectionContent">
                                        {renderInsuredList}
                                    </div>
                                </div>
                            </div>
                            <div className="sgHalfWidthRight">
                                <div className="sg-what-is-not-insured sgDocumentSection">
                                    <div className="sgSectionContainer">
                                        <div className="sgSectionIcon sgIconWhatIsNotInsured">
                                            <h3 id={`notInsuredTitle_${data.header.suffix}`}>{translator(messages.WhatIsNotInsuredHeading)}</h3>
                                        </div>
                                        <div className="sgSectionContent">
                                            {renderNotInsuredList}
                                        </div>
                                    </div>
                                </div>
                                <div className="sg-any-restrictions sgDocumentSection">
                                    <div className="sgSectionContainer">
                                        <div className="sgSectionIcon sgIconAnyRestrictions">
                                            <h3 id={`restrictionsTitle_${data.header.suffix}`}>{translator(messages.AnyRestrictionsHeading)}</h3>
                                        </div>
                                        <div className="sgSectionContent">
                                            {renderRestrictionsList}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="sgDocumentSection">
                            <div className="sgSectionContainer">
                                <div className="sgSectionIcon sgIconWhereCovered">
                                    <h3 id={`coveredTitle_${data.header.suffix}`}>{translator(messages.WhatCoveredHeading)}</h3>
                                </div>
                                <div className="sgSectionContent">
                                    {renderCoveredList}
                                </div>
                            </div>
                        </div>
                        <div className="sgDocumentSection">
                            <div className="sgSectionContainer">
                                <div className="sgSectionIcon sgIconMyObligations">
                                    <h3 id={`obligationsTitle_${data.header.suffix}`}>{translator(messages.ObligationsHeading)}</h3>
                                </div>
                                <div className="sgSectionContent">
                                    {renderObligationsList}
                                </div>
                            </div>
                        </div>
                        <div className="sgDocumentSection">
                            <div className="sgSectionContainer">
                                <div className="sgSectionIcon sgIconHowPay">
                                    <h3 id={`payTitle_${data.header.suffix}`}>{translator(messages.HowDoIPayHeading)}</h3>
                                </div>
                                <div className="sgSectionContent">
                                    {data.howDoIPay}
                                </div>
                            </div>
                        </div>
                        <div className="sgDocumentSection">
                            <div className="sgSectionContainer">
                                <div className="sgSectionIcon sgIconCoverStart">
                                    <h3 id={`csdTitle_${data.header.suffix}`}>{translator(messages.CoverEndStartHeading)}</h3>
                                </div>
                                <div className="sgSectionContent">
                                    { data.endDateMessage || translator(messages.CoverEndStartContent,
                                        { startDate: data.date.periodStartDate, endDate: data.date.periodEndDate })}
                                </div>
                            </div>
                        </div>
                        <div className="sgDocumentSection">
                            <div className="sgSectionContainer">
                                <div className="sgSectionIcon sgIconHowCancel">
                                    <h3 id={`howToCancel_${data.header.suffix}`}>
                                        {translator(messages.CancelContractHeading)}
                                    </h3>
                                </div>
                                <div className="sgSectionContent">
                                    { data.howToCancel || translator(messages.HowToCancelText, {
                                        phone: isMobile
                                            ? <a href={`tel:${data.phoneNumber.replaceAll(' ', '')}`}>{data.phoneNumber}</a>
                                            : data.phoneNumber
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="sgBottomButtonsContainer sgButtonRightOnly">
                            <Button className="sgButton sgTransactionButton" id="IpidCloseButtonBottom" onClick={onClose}>
                                {translator(messages.CloseButton)}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SgIpidDetails;
